import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, UserPlus, Mail, Phone, Shield, User, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/services/api';

const PERFIS = [
  { value: 'cliente', label: 'Cliente', icon: User, color: 'bg-blue-100 text-blue-700', requer: 'telefone' },
  { value: 'entregador', label: 'Entregador', icon: User, color: 'bg-green-100 text-green-700', requer: 'telefone' },
  { value: 'funcionario', label: 'Funcionário', icon: User, color: 'bg-slate-100 text-slate-700', requer: 'telefone' },
  { value: 'super_admin', label: 'Super Admin', icon: Shield, color: 'bg-amber-100 text-amber-700', requer: 'email' },
  { value: 'master_admin', label: 'Master Admin', icon: Shield, color: 'bg-red-100 text-red-700', requer: 'email' },
];

const GestaoUsuarios = () => {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCriar, setOpenCriar] = useState(false);
  const [form, setForm] = useState({
    nome: '',
    perfil: 'cliente',
    telefone: '',
    email: '',
    password: '',
    razao_social: '',
    cnpj: '',
  });

  const carregar = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('rota017_token');
      const res = await api.get('/admin/usuarios', { headers: { Authorization: `Bearer ${token}` } });
      setUsuarios(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const perfilAtual = PERFIS.find(p => p.value === form.perfil) || PERFIS[0];
  const requerEmail = perfilAtual.requer === 'email';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('rota017_token');
      const payload = {
        nome: form.nome.trim(),
        perfil: form.perfil,
      };
      if (requerEmail) {
        if (!form.email || !form.password) {
          toast.error('Admin requer e-mail e senha');
          setLoading(false);
          return;
        }
        payload.email = form.email.trim().toLowerCase();
        payload.password = form.password;
      } else {
        if (!form.telefone) {
          toast.error('Cliente/Entregador requer telefone');
          setLoading(false);
          return;
        }
        payload.telefone = form.telefone.replace(/\D/g, '');
        if (form.razao_social) payload.razao_social = form.razao_social;
        if (form.cnpj) payload.cnpj = form.cnpj.replace(/\D/g, '');
      }
      await api.post('/admin/usuarios', payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Usuário criado com sucesso!');
      setOpenCriar(false);
      setForm({ nome: '', perfil: 'cliente', telefone: '', email: '', password: '', razao_social: '', cnpj: '' });
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar usuário');
    } finally {
      setLoading(false);
    }
  };

  const badgeFor = (perfil) => {
    const p = PERFIS.find(x => x.value === perfil);
    return p ? p : { label: perfil, color: 'bg-slate-100 text-slate-700' };
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b px-4 sm:px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/super-admin')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Gestão de Usuários</h1>
            <p className="text-xs text-slate-500">Criar e visualizar usuários de qualquer nível</p>
          </div>
        </div>
        <Button onClick={() => setOpenCriar(true)} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-novo-usuario">
          <UserPlus className="w-4 h-4 mr-2" />
          Novo Usuário
        </Button>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {loading && usuarios.length === 0 ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-orange-600 animate-spin" />
          </div>
        ) : (
          <div className="bg-white rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-xs uppercase text-slate-600">
                  <th className="px-4 py-3">Nome</th>
                  <th className="px-4 py-3">Perfil</th>
                  <th className="px-4 py-3">Contato</th>
                  <th className="px-4 py-3">Empresa</th>
                  <th className="px-4 py-3">Criado em</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Nenhum usuário cadastrado.</td></tr>
                )}
                {usuarios.map((u) => {
                  const b = badgeFor(u.perfil);
                  return (
                    <tr key={u.id} className="border-t hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">{u.nome}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${b.color}`}>{b.label}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {u.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</div>}
                        {u.telefone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{u.telefone}</div>}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 font-mono">{u.empresa_id?.substring(0, 8)}…</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{u.criado_em ? new Date(u.criado_em).toLocaleDateString('pt-BR') : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Modal Novo Usuário */}
      <Dialog open={openCriar} onOpenChange={setOpenCriar}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-orange-600" />
              Novo Usuário
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Perfil</label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {PERFIS.map(p => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => setForm({ ...form, perfil: p.value })}
                    className={`p-3 rounded-xl border-2 text-left transition ${
                      form.perfil === p.value
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`perfil-${p.value}`}
                  >
                    <p className="text-sm font-semibold text-slate-900">{p.label}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">Login: {p.requer}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">Nome completo</label>
              <Input
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                required
                className="h-11 mt-1"
                data-testid="input-nome"
              />
            </div>

            {requerEmail ? (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-700">E-mail</label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    className="h-11 mt-1"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Senha</label>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                    minLength={8}
                    className="h-11 mt-1"
                    data-testid="input-password"
                  />
                  <p className="text-xs text-slate-500 mt-1">Mínimo 8 caracteres. Use forte para Master Admin.</p>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-700">Telefone (com DDI+DDD)</label>
                  <Input
                    type="tel"
                    placeholder="5511999999999"
                    value={form.telefone}
                    onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                    required
                    className="h-11 mt-1"
                    data-testid="input-telefone"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Razão social</label>
                    <Input
                      value={form.razao_social}
                      onChange={(e) => setForm({ ...form, razao_social: e.target.value })}
                      placeholder="(opcional)"
                      className="h-11 mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">CNPJ</label>
                    <Input
                      value={form.cnpj}
                      onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
                      placeholder="(opcional)"
                      className="h-11 mt-1"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setOpenCriar(false)} disabled={loading}>
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-orange-600 hover:bg-orange-700"
                disabled={loading || !form.nome}
                data-testid="btn-salvar-usuario"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Criar</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestaoUsuarios;
